import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import HeaderComp from "./Components/HeaderComp";
import Footer from "./Components/Footer";
import JoinCourseCard from "./Components/JoinCourseCard";
import { UserAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { priceIfIJoin, minSharePrice, formatPieces } from "../utils/pricing";
import { formatCourseDate, sessionStatus } from "../utils/courseTime";
import { FaUserGraduate, FaCalendarAlt, FaCoins, FaSearch } from "react-icons/fa";
import { BsStars } from "react-icons/bs";

const CourseCard = ({ course, profName, onJoin }) => {
  const myPrice = priceIfIJoin(course);
  const lowest = minSharePrice(course);
  const spotsLeft = course.maxStudents - (course.students || 0);
  const { t, lang } = useLang();

  return (
    <div className="bg-white rounded-2xl shadow-carte hover:shadow-carte-hover transition-shadow overflow-hidden flex flex-col">
      {/* Header coloré */}
      <div className="bg-prune px-5 py-4">
        <h2 className="text-white font-bold text-lg">{course.name}</h2>
        <p className="text-white/80 text-sm mt-1">{profName || t("Professeur")}</p>
      </div>

      <div className="p-5 flex flex-col gap-3 flex-1">
        <p className="text-stone-600 text-sm leading-relaxed line-clamp-2">
          {course.description || t("Aucune description.")}
        </p>

        <div className="flex flex-col gap-2 text-sm mt-auto">
          <div className="flex items-center gap-2 text-stone-500">
            <FaCalendarAlt className="text-prune shrink-0" />
            <span>{formatCourseDate(course, lang)}</span>
          </div>
          <div className="flex items-center gap-2 text-stone-500">
            <FaUserGraduate className="text-prune shrink-0" />
            <span>{course.students || 0}/{course.maxStudents} {t("élèves")}</span>
          </div>
        </div>

        {/* Prix partagé */}
        <div className="bg-creme rounded-xl px-4 py-3 text-sm">
          <div className="flex justify-between text-stone-500">
            <span className="flex items-center gap-2"><FaCoins className="text-or" /> {t("Prix du cours")}</span>
            <span>{formatPieces(course.price, lang)}</span>
          </div>
          <div className="flex justify-between font-bold text-prune mt-1">
            <span>{t("Tu payes")}</span>
            <span>{formatPieces(myPrice, lang)}</span>
          </div>
          {myPrice > lowest && (
            <p className="text-xs text-stone-400 mt-2 leading-snug">
              {t("Chaque nouvel inscrit fait baisser ta part (jusqu'à {min}) : tu es remboursé·e de la différence.", { min: formatPieces(lowest, lang) })}
            </p>
          )}
        </div>

        {/* Barre de remplissage */}
        <div className="mt-2">
          <div className="flex justify-between text-xs text-stone-400 mb-1">
            <span>{t("{n} inscrits", { n: course.students || 0 })}</span>
            <span>{spotsLeft > 1 ? t("{n} places restantes", { n: spotsLeft }) : t("{n} place restante", { n: spotsLeft })}</span>
          </div>
          <div className="w-full bg-stone-100 rounded-full h-2">
            <div
              className="bg-prune h-2 rounded-full transition-all"
              style={{ width: `${Math.min(((course.students || 0) / course.maxStudents) * 100, 100)}%` }}
            />
          </div>
        </div>

        <button
          onClick={() => onJoin(course)}
          disabled={spotsLeft === 0}
          className={`mt-3 w-full py-2.5 rounded-full font-semibold text-sm transition-all ${
            spotsLeft === 0
              ? "bg-stone-200 text-stone-400 cursor-not-allowed"
              : "bg-prune text-white hover:bg-prune-dark"
          }`}
        >
          {spotsLeft === 0 ? t("Complet") : t("Rejoindre le cours")}
        </button>
      </div>
    </div>
  );
};

const UpcomingCourses = () => {
  const [courses, setCourses] = useState([]);
  const [profs, setProfs] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [loading, setLoading] = useState(true);
  const { getAllProfs } = UserAuth();
  const { t } = useLang();

  // Recherche lancée depuis la barre du header
  useEffect(() => {
    setSearch(searchParams.get("q") || "");
  }, [searchParams]);

  useEffect(() => {
    const load = async () => {
      try {
        const allProfs = await getAllProfs();
        setProfs(allProfs);

        const snapshot = await getDocs(collection(db, "courses"));
        const all = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Seulement les cours qui ont encore des places
        setCourses(
          all
            .filter((c) => (c.students || 0) < c.maxStudents)
            .filter((c) => sessionStatus(c) !== "ended")
            .sort((a, b) =>
              `${a.date || ""}${a.time || ""}`.localeCompare(`${b.date || ""}${b.time || ""}`)
            )
        );
      } catch (e) {
        console.error("Erreur:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fiches publiques (id = uid du prof) : on ne montre jamais l'e-mail du prof
  const getProfName = (course) => {
    const p = profs.find((p) => p.id === course.ownerId);
    return p ? `${p.firstName} ${p.lastName}` : "";
  };

  const term = search.trim().toLowerCase();
  const filtered = courses.filter(
    (c) =>
      !term ||
      c.name?.toLowerCase().includes(term) ||
      c.subject?.toLowerCase().includes(term) ||
      c.description?.toLowerCase().includes(term) ||
      getProfName(c).toLowerCase().includes(term)
  );

  return (
    <div className="min-h-screen bg-creme">
      <HeaderComp />

      {/* Hero */}
      <div className="bg-prune text-white py-12 px-6 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <BsStars className="text-or" size={24} />
          <h1
            className="text-4xl md:text-5xl"
            style={{ fontFamily: "Alegreya, Georgia, serif", fontWeight: 700 }}
          >
            {t("Cours à venir")}
          </h1>
          <BsStars className="text-or" size={24} />
        </div>
        <p className="text-white/80 text-base max-w-xl mx-auto">
          {t("Des cours en petits groupes pour apprendre ensemble. Plus on est nombreux, moins c'est cher !")}
        </p>

        {/* Barre de recherche */}
        <div className="mt-6 max-w-md mx-auto relative">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            placeholder={t("Rechercher un cours...")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-full text-stone-800 text-sm shadow-carte focus:outline-none focus:ring-2 focus:ring-or"
          />
        </div>
      </div>

      {/* Contenu */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-20 text-stone-400">{t("Chargement des cours...")}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">🔮</div>
            <p className="text-stone-500 text-lg">{t("Aucun cours trouvé pour l'instant.")}</p>
            <p className="text-stone-400 text-sm mt-2">{t("Reviens bientôt ou contacte un professeur !")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                profName={getProfName(course)}
                onJoin={(c) => setSelectedCourse(c)}
              />
            ))}
          </div>
        )}
      </div>

      <Footer />

      {/* Modal d'inscription — uniquement pour le cours sélectionné */}
      {selectedCourse && (
        <JoinCourseCard
          course={selectedCourse}
          onClose={() => setSelectedCourse(null)}
        />
      )}
    </div>
  );
};

export default UpcomingCourses;
